import { AppForm } from '../../../../../../../common/forms/app.form';
import { Injectable, Signal } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
  ApplicationAppEntityApplicationRead,
  ApplicationAppEntityApplicationWrite,
  ParcelAppEntityParcelWrite
} from '../../../../../../../openapi';
import { CustomValidator, fromToDate } from '../../../../../../../common/forms/custom.validators';
import { toNumber } from '../../../../../../../services/helpers';
import {
  DictTownRoadItem, DictTownStreetItem,
  DictTownWithRoadsItem,
  DictTownWithStreetsItem
} from '../../../../../../../services/dictionary.common.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { DictionaryCommonService } from '../../../../../../../services/dictionary.common.service';

export type ProjectTypeItem = 'Stałej' | 'Tymczasowej' | string;

export type LocalisationDataType
  = 'Chcę wprowadzić dane ewidencyjne działki'
  | 'Chcę wprowadzić numer i nazwę drogi'
  | 'Chcę wprowadzić ulicę';

@Injectable()
export class TrafficOrganizationProjectForm extends AppForm {

  public projectTypes: ProjectTypeItem[] = [
    'Tymczasowej',
    'Stałej'
  ];

  public localisationDataTypes: LocalisationDataType[] = [
    'Chcę wprowadzić dane ewidencyjne działki',
    'Chcę wprowadzić numer i nazwę drogi',
    'Chcę wprowadzić ulicę'
  ];

  public townWithRoadsList: Signal<DictTownWithRoadsItem[] | undefined> = toSignal(this.dictService.getTownWithRoadsList());

  public townList: Signal<DictTownWithStreetsItem [] | undefined> = toSignal(this.dictService.getTownWithStreetsList());

  public getStreetList(town: string): DictTownStreetItem[] {
    return this.townList()?.filter(v => v?.name === town)?.at(0)?.streets || [];
  }

  public getRoadList(townName?: string | null): Array<DictTownRoadItem> {
    return this.townWithRoadsList()?.filter(v => v.name === townName).at(0)?.roads || [];
  }

  public getTownList(localisationDataType: string): string[] {
    if (localisationDataType === 'Chcę wprowadzić ulicę') {
      return this.townList()?.map(v => v?.name || '') || [];
    } else {
      return this.townWithRoadsList()?.map(v => v?.name || '') || [];
    }
  }

  constructor(private dictService: DictionaryCommonService) {
    super({
      projectType: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),
      projectTitle: new FormControl('', { validators: [Validators.required, Validators.maxLength(255)] }),
      trafficOrganizationDescription: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(5000)]
      }),

      localisationDataType: new FormControl(''),

      city: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(255)] }),
      roadCategory: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),
      startDate: new FormControl(null, [Validators.required, CustomValidator.minDate(new Date())]),
      investor: new FormControl(null, [Validators.required, Validators.maxLength(255)])
    }, {
      validators: [fromToDate('startDate', 'endDate')]
    });
    this.localisationDataTypeChange(this.localisationDataTypes.at(0) as LocalisationDataType);
  }

  public get city(): FormControl {
    return this.get('city') as FormControl;
  }

  public get roadName(): FormControl | null {
    return this.get('roadName') as FormControl | null;
  }

  public get roadNumber(): FormControl | null {
    return this.get('roadNumber') as FormControl | null;
  }

  public roadNumberChange(roadNumber: string): void {
    const roadName = this.getRoadList(this.city.value).filter(v => v.roadNumber === roadNumber).at(0)?.roadName || '';
    this.roadName?.setValue(roadName);
  }

  public get parcels(): FormControl | null {
    return this.get('parcels') as FormControl | null;
  }

  public get parcelsValues(): ParcelAppEntityParcelWrite[] {
    return this.parcels?.value || [];
  }

  public get street(): FormControl | null {
    return this.get('street') as FormControl | null;
  }

  public localisationDataTypeChange(v: LocalisationDataType): void {
    this.city.setValue('');
    this.city.markAsUntouched();
    this.removeControl('parcels');
    this.removeControl('roadName');
    this.removeControl('roadNumber');
    this.removeControl('street');
    switch (v) {
      case 'Chcę wprowadzić numer i nazwę drogi': {
        this.addControl('roadNumber', new FormControl('', {
          nonNullable: true,
          validators: [Validators.required, Validators.maxLength(255)]
        }));
        this.addControl('roadName', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
        break;
      }
      case 'Chcę wprowadzić dane ewidencyjne działki': {
        this.addControl('parcels', new FormControl<ParcelAppEntityParcelWrite[]>(
          [], {
            nonNullable: true,
            validators: [Validators.required, Validators.minLength(1)]
          }
        ));
        break;
      }
      case 'Chcę wprowadzić ulicę': {
        this.addControl('street', new FormControl('', {
          nonNullable: true,
          validators: [Validators.required, Validators.maxLength(255)]
        }));
        break;
      }
    }
  }

  public get localisationDataType(): FormControl {
    return this.get('localisationDataType') as FormControl;
  }

  public get projectType(): FormControl {
    return this.get('projectType') as FormControl;
  }

  public projectTypeChange(v: ProjectTypeItem): void {
    this.removeControl('endDate');

    this.removeControl('providePointChainage');

    this.removeControl('chainageFrom');
    this.removeControl('chainageTo');
    this.removeControl('chainage');

    this.removeControl('organizerOrContractorType');
    this.removeControl('organizerOrContractorName');


    if (v === 'Tymczasowej') {
      this.addControl('endDate', new FormControl(null, Validators.required));

      this.addControl('chainageFrom', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
      this.addControl('chainageTo', new FormControl(null, [Validators.required, Validators.maxLength(255)]));

      this.addControl('organizerOrContractorType', new FormControl('', {
        nonNullable: true,
        validators: [Validators.maxLength(255)]
      }));
      this.addControl('organizerOrContractorName', new FormControl('', {
        nonNullable: true,
        validators: [Validators.maxLength(255)]
      }));
    }
    if (v === 'Stałej') {
      this.addControl('providePointChainage', new FormControl(true, { nonNullable: true, validators: [] }));
      this.providePointChainageChange(true);
    }
  }

  public get providePointChainage(): FormControl | null {
    return this.get('providePointChainage') as FormControl;
  }

  public providePointChainageChange(v: boolean): void {
    if (v) {
      this.removeControl('chainageFrom');
      this.removeControl('chainageTo');

      this.addControl('chainage', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
    } else {
      this.addControl('chainageFrom', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
      this.addControl('chainageTo', new FormControl(null, [Validators.required, Validators.maxLength(255)]));

      this.removeControl('chainage');
    }
  }

  public get chainageFrom(): FormControl | null {
    return this.get('chainageFrom') as FormControl | null;
  }

  public get chainageTo(): FormControl | null {
    return this.get('chainageTo') as FormControl | null;
  }

  public get chainage(): FormControl | null {
    return this.get('chainage') as FormControl | null;
  }

  public get endDate(): FormControl | null {
    return this.get('endDate') as FormControl | null;
  }

  public get startDate(): FormControl | null {
    return this.get('startDate') as FormControl | null;
  }

  public get organizerOrContractorType(): FormControl | null {
    return this.get('organizerOrContractorType') as FormControl | null;
  }

  public get organizerOrContractorName(): FormControl | null {
    return this.get('organizerOrContractorName') as FormControl | null;
  }

  public toWriteModel(): ApplicationAppEntityApplicationWrite {
    let value = this.value;
    delete value.parcels;
    return {
      ...value,
      roadName: this.roadName?.value || null,
      roadNumber: this.roadNumber?.value || null,
      street: this.street?.value || null,
      chainageFrom: this.chainageFrom ? toNumber(this.chainageFrom.value) : null,
      chainageTo: this.chainageTo ? toNumber(this.chainageTo.value) : null,
      chainage: this.chainage ? toNumber(this.chainage.value) : null
    };
  }

  public override patchFormValues(value: ApplicationAppEntityApplicationRead): void {
    const localisationDataType: LocalisationDataType = (value?.localisationDataType || this.localisationDataTypes.at(0)) as LocalisationDataType;
    this.localisationDataType?.setValue(localisationDataType);
    this.localisationDataTypeChange(localisationDataType);

    super.patchFormValues(value);

    this.roadName?.setValue(value?.roadName || '');
    this.roadNumber?.setValue(value?.roadNumber || '');
    this.parcels?.setValue(value?.parcels);

    this.projectTypeChange(value?.projectType || '');

    this.providePointChainage?.setValue(value?.providePointChainage);
    if (value?.projectType === 'Stałej') {
      this.providePointChainageChange(!!value?.providePointChainage);
    }

    this.chainageFrom?.setValue(value?.chainageFrom?.toString());
    this.chainageTo?.setValue(value?.chainageTo?.toString());
    this.chainage?.setValue(value?.chainage);

    this.endDate?.setValue(value?.endDate);
    this.organizerOrContractorType?.setValue(value?.organizerOrContractorType);
    this.organizerOrContractorName?.setValue(value?.organizerOrContractorName);
  }
}
